import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type SessionMetadataUiController = {
  summaryText: string;
  validationError?: string;
  readOnly: boolean;
  canEditAttendance: boolean;
  openMetadataModal: () => void;
  openAttendanceModal: () => void;
};

type SessionMetadataUiContextValue = {
  controller: SessionMetadataUiController | null;
  setController: (next: SessionMetadataUiController | null) => void;
};

const SessionMetadataUiContext = createContext<SessionMetadataUiContextValue | null>(null);

export function SessionMetadataUiProvider({ children }: { children: ReactNode }): JSX.Element {
  const [controller, setControllerState] = useState<SessionMetadataUiController | null>(null);
  const setController = useCallback((next: SessionMetadataUiController | null) => {
    setControllerState(next);
  }, []);
  const value = useMemo(() => ({ controller, setController }), [controller, setController]);
  return (
    <SessionMetadataUiContext.Provider value={value}>{children}</SessionMetadataUiContext.Provider>
  );
}

export function useSessionMetadataUi(): SessionMetadataUiContextValue | null {
  return useContext(SessionMetadataUiContext);
}

/** Stable setter — safe to use in effects without depending on controller identity. */
export function useSessionMetadataUiSetController():
  | ((next: SessionMetadataUiController | null) => void)
  | undefined {
  return useContext(SessionMetadataUiContext)?.setController;
}

export function useSessionMetadataUiController(): SessionMetadataUiController | null {
  return useContext(SessionMetadataUiContext)?.controller ?? null;
}
